import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Activity, Clock, AlertCircle } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

/**
 * Public uptime status page, intended to be linked from the footer
 * and shared with B2B partners as a trust signal.
 */
const StatusPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/public/status?window_minutes=60`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
      } catch (err) {
        setError(err.message);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [API]);

  const overall = data?.summary?.overall_ok;
  const statusLabel = overall
    ? 'All systems operational'
    : data?.summary?.down
    ? `${data.summary.down} path(s) degraded`
    : 'No data yet';
  const statusColor = overall
    ? 'from-green-500 to-emerald-500'
    : data?.summary?.down
    ? 'from-red-500 to-rose-500'
    : 'from-slate-400 to-slate-500';

  return (
    <div className="min-h-screen bg-capsule-bg" data-testid="status-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-10 md:py-16">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-2xl flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            System Status
          </h1>
        </div>
        <p className="text-gray-600 mb-8">
          Real-time uptime monitor covering the Penlab Peptide storefront,
          admin panel, affiliate portal, and search-engine feeds.
        </p>

        {/* Overall banner */}
        <div className={`rounded-2xl p-6 mb-8 bg-gradient-to-r ${statusColor} text-white shadow-lg`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              {overall ? (
                <CheckCircle2 className="w-9 h-9" />
              ) : data?.summary?.down ? (
                <XCircle className="w-9 h-9" />
              ) : (
                <AlertCircle className="w-9 h-9" />
              )}
              <div>
                <div className="text-xl md:text-2xl font-bold" data-testid="status-headline">
                  {statusLabel}
                </div>
                <div className="text-sm text-white/80 mt-0.5">
                  {data?.generated_at
                    ? `Last updated ${new Date(data.generated_at).toLocaleTimeString()}`
                    : 'Loading...'}
                </div>
              </div>
            </div>
            {data?.summary && (
              <div className="text-right">
                <div className="text-sm text-white/80">Avg response</div>
                <div className="text-2xl font-bold">{data.summary.avg_ms} ms</div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-red-700 mb-6">
            Could not load status data: {error}
          </div>
        )}

        {data?.message && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-amber-800 mb-6">
            {data.message}
          </div>
        )}

        {/* Per-check rows */}
        {data?.checks?.length > 0 && (
          <div className="glass-card rounded-2xl divide-y divide-gray-200/70 overflow-hidden">
            {data.checks.map((c) => (
              <div
                key={c.path}
                className="flex items-center justify-between px-5 py-4 hover:bg-white/40 transition"
                data-testid={`status-row-${c.path.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {c.ok ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{c.label}</div>
                    <div className="text-xs text-gray-500 font-mono truncate">{c.path}</div>
                  </div>
                </div>
                <div className="flex items-center gap-6 flex-shrink-0 text-sm">
                  <div className="text-right hidden sm:block">
                    <div className="text-xs text-gray-500">Uptime (60m)</div>
                    <div className={`font-bold ${c.uptime_pct >= 99 ? 'text-green-600' : c.uptime_pct >= 95 ? 'text-amber-600' : 'text-red-600'}`}>
                      {c.uptime_pct}%
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" /> avg
                    </div>
                    <div className="font-semibold text-gray-900">{c.avg_ms} ms</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-6 text-xs text-gray-500 text-center">
          Auto-refreshes every 30 seconds · Monitor: Penlab Peptide internal deploy_monitor
        </p>
      </div>
      <Footer />
    </div>
  );
};

export default StatusPage;
